import { z } from "zod";
import {
  patientSourceSectionSchema,
  type PatientSourceSection,
} from "./medical-sources";
import {
  localizedTextSchema,
  svRequiredLocalizedTextSchema,
  type LocalizedText,
} from "./intake-template";

// CAR-119 / CAR-120 — editable lesson/article content. Closest analog is the
// IntakeFormTemplate stack (DB-stored, tenant-scoped, localized, versioned,
// admin-authored) but deliberately simpler: ONE living row per (tenant, key)
// rather than a row-per-version. `status` gates patient/public visibility and
// `version` is a monotonic edit counter (ETag / cache-bust + audit). Bodies are
// MARKDOWN. No PII — pure tenant content served to mobile + web.
//
// Localized copy reuses `localizedTextSchema` / `LocalizedText` from
// intake-template (the established `{ sv, en }` convention).

export const ARTICLE_TYPES = [
  "mini_lesson",
  "checklist",
  "education_card",
  "coach_message",
  // TES-190 — general web-facing resource articles (the landing "Articles"
  // module): longer-form educational pieces, distinct from in-app lessons.
  "article",
  // QA-baseline 2026-08-19 (CP-09 A/B/C) — a CLINICAL RESULT VIEW: rendered by
  // the same full-view renderer but never a knowledge-bank article. Excluded
  // from every public list/search surface; by-key reads are patient-/case-
  // bound (the patient must hold a FlowAssignment whose materialized items
  // reference the key) and served only on the AUTHENTICATED patient route.
  // Unauthorized reads 404 (never 403 — existence must not leak).
  "clinical_result",
  // Support programmes (pre-launch QA import 2026-08-20) — the "Kost" and
  // "Träning" sections render off dedicated types so none of this floods the
  // type-scoped "Kunskapsbank" lists. All patient copy is authored content,
  // iterable via re-import without an app build:
  //   recipe         — the recipe bank (key: recipe.mvp.<mealType>.rNN; the
  //                    meal segment drives client grouping, v16 tag-style)
  //   nutrition_plan — the three static meal plans
  //   training_pass  — workout sessions with the exercises composed into the
  //                    body
  //   support_copy   — section intro/overview blobs the "Stöd" screens fetch
  //                    by known key (support.nutrition.overview, …)
  "recipe",
  "nutrition_plan",
  "training_pass",
  //  training_exercise — one exercise in a session (option B): the structure
  //                    fields (dose, easier option, start/end image) ride
  //                    authorMetadata and are projected to `training` — the
  //                    same pattern as the mini-courses' `course`.
  "training_exercise",
  "support_copy",
  //   short_guide    — short women-specific guides (guide.<section>.<slug>;
  //                    the section segment splits them across "Stöd → Kost"
  //                    and "Stöd → Träning")
  "short_guide",
  //   coach_template — TES-1355: a coach message template (key
  //                    coach_template.<name>). Staff-only: never on a public
  //                    or patient read, and written only through the
  //                    coach-template routes (contracts' coach-template.ts).
  "coach_template",
] as const;
export const articleTypeSchema = z.enum(ARTICLE_TYPES);
export type ArticleType = (typeof ARTICLE_TYPES)[number];

export const articleStatusSchema = z.enum(["draft", "published"]);
export type ArticleStatus = z.infer<typeof articleStatusSchema>;

// Optional structured call-to-action rendered as the article's primary button
// (replaces the old literal "CTA: …" trailing line convention in markdown
// bodies — a typed, admin-editable CTA beats a parsing convention). `route` is
// a deliberate whitelist of mobile AppStack routes that take no required
// params; when absent the CTA renders inert (a labelled, non-navigating
// button). `label` is the localized button text.
export const ARTICLE_CTA_ROUTES = [
  "FindLab",
  "WeeklyCheckin",
  "Provsvar",
  "Chatt",
  "ThenVsNow",
  // Ö-140 — "Din första injektion – steg för steg" carries the button
  // "Jag har tagit min första dos". It pointed at WeeklyCheckin and recorded
  // nothing; it now opens the registration view, where the date is confirmed.
  "FirstDose",
  // TES-1313 — "Så mäter du blodtryck och vilopuls" ends in "Registrera
  // värden", which opens the CP-06 blood-pressure form. Every shipped build
  // (2.0.8 and 2.0.9 included) has this AppStack route with optional params
  // and navigates to `cta.route` by name, so no client-contract gate is needed.
  "RecordBloodPressure",
] as const;
export const articleCtaRouteSchema = z.enum(ARTICLE_CTA_ROUTES);
export type ArticleCtaRoute = (typeof ARTICLE_CTA_ROUTES)[number];

export interface ArticleCta {
  label: LocalizedText; // { sv, en } button label
  route?: ArticleCtaRoute; // inert when absent
}
export const articleCtaSchema = z.object({
  label: localizedTextSchema,
  route: articleCtaRouteSchema.optional(),
});

// Full admin/editor shape.
export interface Article {
  id: string;
  key: string;
  type: ArticleType;
  title: LocalizedText; // { sv, en }
  body: LocalizedText; // markdown { sv, en }
  cta?: ArticleCta; // optional primary call-to-action button
  status: ArticleStatus;
  version: number;
  updatedBy: string | null;
  updatedAt: string; // ISO
}
export const articleSchema = z.object({
  id: z.string(),
  key: z.string().min(1),
  type: articleTypeSchema,
  title: localizedTextSchema,
  body: localizedTextSchema,
  cta: articleCtaSchema.optional(),
  status: articleStatusSchema,
  version: z.number().int().positive(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string(),
});

// Admin list row (no bodies — keep the list payload small).
export const articleSummarySchema = articleSchema.pick({
  key: true,
  type: true,
  title: true,
  status: true,
  version: true,
  updatedAt: true,
});
export type ArticleSummary = z.infer<typeof articleSummarySchema>;

// Create/update input. `key` is required on create, immutable on update.
// status is NOT settable here — the publish/unpublish endpoints own it.
// TES-361 — authored title/body require non-empty `sv` (en falls back to sv
// at render time since TES-362; empty sv would be a truly blank article).
export const articleUpsertSchema = z.object({
  key: z
    .string()
    .min(1)
    .regex(/^[a-z0-9_]+(\.[a-z0-9_]+)*$/), // dotted snake key, e.g. "blood_test.why_tests"
  type: articleTypeSchema,
  title: svRequiredLocalizedTextSchema,
  body: svRequiredLocalizedTextSchema,
  cta: articleCtaSchema.optional(),
  // TES-404 — authored taxonomy from the Drive imports (content_type,
  // content_category, app_collection, saveable, …). Opaque passthrough:
  // stored as-is; consumers read specific keys when they need them.
  authorMetadata: z.record(z.string(), z.unknown()).optional(),
});
export type ArticleUpsertInput = z.infer<typeof articleUpsertSchema>;

// TES-207 — coach-message (and general article) editing gets a draft→publish
// flow with version history + rollback. The live `Article` row is unchanged
// (it still holds the current PUBLISHED content the patient/coach read path
// serves). History + the working draft live in a side table (`ArticleRevision`)
// so no read site changes:
//   - exactly one `draft` revision per article = the working edit buffer;
//   - one immutable `published` revision per published version = the history.
// Publishing promotes the draft into the live row (version+1) and freezes the
// draft as the new published snapshot. Rollback is forward-only: it drafts an
// old revision's content so publishing it lands a NEW version with old copy.

// Editable working copy (the draft buffer the admin edits before publishing).
export const articleDraftSchema = z.object({
  title: localizedTextSchema,
  body: localizedTextSchema,
  cta: articleCtaSchema.optional(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string(), // ISO
});
export type ArticleDraft = z.infer<typeof articleDraftSchema>;

// One immutable published snapshot in an article's history.
export interface ArticleRevision {
  version: number;
  title: LocalizedText;
  body: LocalizedText;
  cta?: ArticleCta;
  changedBy: string | null;
  changedAt: string; // ISO
}
export const articleRevisionSchema = z.object({
  version: z.number().int().positive(),
  title: localizedTextSchema,
  body: localizedTextSchema,
  cta: articleCtaSchema.optional(),
  changedBy: z.string().nullable(),
  changedAt: z.string(),
});

// History list row (no bodies).
export const articleRevisionSummarySchema = articleRevisionSchema.pick({
  version: true,
  changedBy: true,
  changedAt: true,
});
export type ArticleRevisionSummary = z.infer<
  typeof articleRevisionSummarySchema
>;

// Full editor view: the live published article, the working draft (if any),
// and how many published versions exist (for the history affordance).
export const articleEditorViewSchema = z.object({
  article: articleSchema,
  draft: articleDraftSchema.nullable(),
  publishedVersions: z.number().int().nonnegative(),
});
export type ArticleEditorView = z.infer<typeof articleEditorViewSchema>;

// Rollback input — draft the content of an earlier published version.
export const articleRollbackSchema = z.object({
  toVersion: z.number().int().positive(),
});
export type ArticleRollbackInput = z.infer<typeof articleRollbackSchema>;

// TES-397 — "Spara fördjupningar": the patient's saved-article keys, newest
// first. Keys only — the client already holds the published article list, so
// the saved section joins locally instead of duplicating content payloads.
export const savedArticlesResponseSchema = z.object({
  keys: z.array(z.string()),
});
export type SavedArticlesResponse = z.infer<typeof savedArticlesResponseSchema>;

// TES-403 — the optional input module INSIDE a deepening (the v13
// contract: the home card stays a reading CTA; input happens here). Authored
// as `authorMetadata.interaction` on the Article (structured — the import
// transform parses the raw contract once) and served pre-validated. Swedish
// strings only for now (TES-362 en→sv fallback applies).
// v2.4 reflection contract (ella.patient_reflection.v1) vocabulary. Mirrors
// the free-string columns on PatientReflection; kept here so client and server
// agree on the values without a Prisma enum.
export const reflectionSourceTypeSchema = z.enum([
  "dt_deepening",
  "course_lesson",
]);
export type ReflectionSourceType = z.infer<typeof reflectionSourceTypeSchema>;

export const reflectionInputMethodSchema = z.enum(["text", "voice_transcript"]);
export type ReflectionInputMethod = z.infer<typeof reflectionInputMethodSchema>;

export const reflectionVisibilitySchema = z.enum([
  "private",
  "shared_with_coach",
]);
export type ReflectionVisibility = z.infer<typeof reflectionVisibilitySchema>;

export const articleInteractionSchema = z.object({
  // Stable prompt identity within the article. Defaulted, because every
  // article authored so far carries exactly one interaction block.
  promptId: z.string().min(1).default("reflection"),
  prompt: z.string().min(1),
  // Choice chips; empty ⇒ free-text only.
  options: z.array(z.string().min(1)).default([]),
  allowFreeText: z.boolean().default(false),
  submitCta: z.string().nullable().default(null),
  confirmation: z.string().nullable().default(null),
  // The visible return — the patient must always see something happen.
  visibleReturn: z.string().nullable().default(null),
  // Contract flags (MVP: stored on the answer; no automatic program change).
  saveAsInsight: z.boolean().default(true),
  saveAsReflection: z.boolean().default(true),
  coachVisible: z.boolean().default(false),
});
export type ArticleInteraction = z.infer<typeof articleInteractionSchema>;

// TES-1541 (Ö-830 (c)) — GET patient-api/v1/articles/:key/reflection/prompt:
// the authored question on its own, for the DT card's "Reflektera" screen.
// The question stays on the dt.* deepening it was authored on (same promptId,
// so earlier Dagbok answers stay linked), but the screen never opens that
// page: "Ingen dold gammal fördjupningssida krävs."
export const articleReflectionPromptSchema = z.object({
  articleKey: z.string(),
  interaction: articleInteractionSchema,
});
export type ArticleReflectionPrompt = z.infer<
  typeof articleReflectionPromptSchema
>;

// v17 minikurser — the stepped-lesson structure authored on `authorMetadata`
// (content_type / course_key / lesson_index / next_lesson_key / …), projected
// onto the public read shape.
//
// It has to be projected: `authorMetadata` is admin-only (it carries editorial
// fields like review flags and purpose notes that patients must never see) and
// the import's `data.contentType` is silently STRIPPED by articleUpsertSchema.
// Without this block a lesson is indistinguishable from its course overview,
// and the reader flattens a whole course library into one undifferentiated
// list. `type` stays `mini_lesson` for every one of them (backwards compat) —
// `course.role` is the real discriminator.
//
// A lesson carries its own position AND its neighbours' keys so the reader can
// render "Lektion 2 av 4" with "Nästa"/"Föregående" from a single article
// fetch.
// TES-614 — reading time per locale. Minutes differ between sv and en because
// the two bodies are different lengths, and the bar states one number, so the
// projection carries both rather than picking for the reader.
export const localizedMinutesSchema = z.object({
  sv: z.number().int().nonnegative(),
  en: z.number().int().nonnegative(),
});
export type LocalizedMinutes = z.infer<typeof localizedMinutesSchema>;

// ~130 words/min is a conservative Swedish prose pace; every surface only ever
// claims an approximation ("ca X min").
//
// This lives in contracts because BOTH the API (projecting a lesson's course
// bar) and the app (the course list and overview) state a course's length, and
// two implementations of "how long is this" drift into two different answers
// on two screens the patient sees minutes apart.
export const READING_WORDS_PER_MINUTE = 130;

export function articleReadMinutes(body: string): number {
  const words = body.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / READING_WORDS_PER_MINUTE));
}

// A course's time is the SUM of its lessons' times — not the same algorithm
// re-run over the concatenated bodies. Those two are NOT equal: rounding once
// at the end loses the per-lesson `Math.max(1, …)` floor, so four ~90-word
// lessons list as 1+1+1+1 while a single pass claims 3. The lesson list is what
// the patient adds up, so the sum is canonical.
export function courseReadMinutes(bodies: string[]): number {
  return bodies.reduce((n, body) => n + articleReadMinutes(body), 0);
}

export const articleCourseSchema = z.discriminatedUnion("role", [
  z.object({
    role: z.literal("course"),
    courseKey: z.string().min(1),
    lessonKeys: z.array(z.string().min(1)).default([]),
    lessonCount: z.number().int().nonnegative().default(0),
  }),
  z.object({
    role: z.literal("lesson"),
    courseKey: z.string().min(1),
    index: z.number().int().positive(),
    count: z.number().int().positive(),
    nextKey: z.string().nullable().default(null),
    previousKey: z.string().nullable().default(null),
    // TES-614 — what the locked course bar shows above a lesson: the course's
    // own name and how long the WHOLE course takes. Neither is authored on the
    // lesson (only `course_key` is), and the reader fetches one article, so
    // without these the bar would have to pull the entire mini_lesson library
    // on every lesson open just to read two values off it.
    //
    // Nullable with a null default on purpose: a lesson whose overview is not
    // published yet has no course title, and every projection written before
    // this field existed still parses.
    courseTitle: localizedTextSchema.nullable().default(null),
    estimatedMinutes: localizedMinutesSchema.nullable().default(null),
  }),
]);
export type ArticleCourse = z.infer<typeof articleCourseSchema>;

// The training experience (option B): a session is a `training_pass` article
// (body = the session overview), and each exercise is its own
// `training_exercise` article. The structure fields are authored in
// authorMetadata and projected here — `training.role` is the discriminator,
// the same model as the mini-courses' `course`. An exercise carries its
// position AND its neighbours' keys so the stepper can render "Övning 2 av 4"
// with "Nästa"/"Föregående" from a single list. Image references are media
// keys (QA: the shared placeholder assets training.placeholder.start/end.v1
// until real images are delivered).
export const articleTrainingSchema = z.discriminatedUnion("role", [
  z.object({
    role: z.literal("pass"),
    passKey: z.string().min(1),
    exerciseKeys: z.array(z.string().min(1)).default([]),
    exerciseCount: z.number().int().nonnegative().default(0),
    // Support IA v2.1: structured version choices — "Kort" (1 round) / "Hela"
    // (2 rounds) with a time estimate. The canonical term is "varv" (rounds),
    // not sets. Empty list = the session has no version data (the client
    // falls back to a single run-through with no version choice).
    versions: z
      .array(
        z.object({
          id: z.string().min(1), // "short" | "full" (open to more)
          label: z.string().min(1), // "Kort" / "Hela"
          rounds: z.number().int().positive(),
          estimatedMinutesMin: z.number().int().positive(),
          estimatedMinutesMax: z.number().int().positive(),
        }),
      )
      .default([]),
    // Rest guidance at SESSION level (lifted out of the last exercise's body).
    restGuidance: z.string().nullable().default(null),
    // TES-612 — the ONE-LINE card summary on the programme overview
    // ("Ben, armar och bål med stol och vägg."). Wireframe 08's card copy is
    // not the body's opening sentence shortened: pass 2's reads differently
    // altogether. So it is its own authored field, and the card falls back to
    // the body's first sentence while it is unauthored — the same shape as a
    // recipe's `shortIntro`.
    summary: z.string().nullable().default(null),
  }),
  z.object({
    role: z.literal("exercise"),
    passKey: z.string().min(1),
    index: z.number().int().positive(),
    count: z.number().int().positive(),
    dose: z.string().min(1),
    cue: z.string().nullable().default(null),
    easierOption: z.string().nullable().default(null),
    mediaStart: z.string().nullable().default(null),
    mediaEnd: z.string().nullable().default(null),
    nextKey: z.string().nullable().default(null),
    previousKey: z.string().nullable().default(null),
  }),
]);
export type ArticleTraining = z.infer<typeof articleTrainingSchema>;

// Structured recipe experience (UX/UI brief 2026-08-24): recipes keep their
// markdown body as a technical fallback, but the dedicated recipe view
// renders ONLY these structure fields. Authored in authorMetadata
// (snake_case) and projected here — the same model as `training`/`course`.
// `order` is the canonical R01–R30 order (the library sorts on it, never on
// updatedAt). `prepMinutes` is the normalized filter value (the leading
// minute figure from prepTime; null when none can be parsed — the recipe is
// then excluded from the time filter, never from the list). `prepTime` is
// null when the source lacks a verified time (ER01–ER84, TES-428: "omit
// unless source-verified") — the view hides the row; no time is made up.
// `imageKey` is a media key; a missing image hides the image module (never
// another dish as a fallback). Nutritional values are DELIBERATELY absent
// (MVP decision).
export const articleRecipeSchema = z.object({
  mealType: z.string().min(1),
  prepTime: z.string().min(1).nullable().default(null),
  prepMinutes: z.number().int().positive().nullable().default(null),
  servings: z.string().min(1),
  tags: z.array(z.string().min(1)).default([]),
  ingredients: z.array(z.string().min(1)).min(1),
  steps: z.array(z.string().min(1)).min(1),
  adaptations: z.array(z.string().min(1)).default([]),
  smallAppetite: z.string().nullable().default(null),
  imageKey: z.string().nullable().default(null),
  shortIntro: z.string().nullable().default(null),
  order: z.number().int().positive(),
});
export type ArticleRecipe = z.infer<typeof articleRecipeSchema>;

// Structured meal plans (Support v2.4): a plan is not a long article but a
// short intro + seven visual day cards with separate meal cards. The
// structure is authored in authorMetadata.plan (snake_case) and projected
// here — the same pattern as `training`/`recipe`. The body stays as a
// fallback until the structured readback is approved, but is not the primary
// renderer. A meal is either a full recipe (RECIPE → recipeKey required,
// image + "Öppna recept") or a simple meal example (SIMPLE_MEAL_EXAMPLE →
// shortInstruction required, never opens a recipe object). `swapGroupId`
// drives the "Byt måltid" selection (same meal type, a small curated
// selection — never the whole library).
export const articlePlanMealSchema = z.object({
  slot: z.string().min(1),
  displayName: z.string().min(1),
  contentKind: z.enum(["RECIPE", "SIMPLE_MEAL_EXAMPLE"]),
  recipeKey: z.string().nullable().default(null),
  shortInstruction: z.string().nullable().default(null),
  imageAssetKey: z.string().nullable().default(null),
  estimatedTime: z.string().nullable().default(null),
  swapGroupId: z.string().min(1),
});
export const articlePlanSchema = z.object({
  shortDescription: z.string().min(1),
  intro: z.string().min(1),
  howToUse: z.string().min(1),
  days: z
    .array(
      z.object({
        dayIndex: z.number().int().positive(),
        title: z.string().min(1),
        meals: z.array(articlePlanMealSchema).min(1),
      }),
    )
    .length(7),
  // "Bra att ha hemma" / "Kom ihåg" etc. — preserved verbatim after the day
  // cards (the line-coverage requirement: no source heading may be dropped in
  // the migration).
  extraSections: z
    .array(z.object({ heading: z.string().min(1), text: z.string().min(1) }))
    .default([]),
});
export type ArticlePlanMeal = z.infer<typeof articlePlanMealSchema>;
export type ArticlePlan = z.infer<typeof articlePlanSchema>;

// TES-1497 — one row of an article's "Läs mer" list. Authored as
// `authorMetadata.read_more` (an ordered list of article keys, from the v16
// knowledge bank's "Läs mer" lines) and resolved server-side to the target's
// CURRENT published title: the doc's own link label is an editorial copy that
// can lag a retitle, and the reader should show what the tap will open.
export const articleRelatedLinkSchema = z.object({
  key: z.string().min(1),
  title: localizedTextSchema,
});
export type ArticleRelatedLink = z.infer<typeof articleRelatedLinkSchema>;

// Public/patient read shape — published content only, no status/updatedBy/id.
export interface PublicArticle {
  key: string;
  type: ArticleType;
  title: LocalizedText;
  body: LocalizedText;
  cta?: ArticleCta; // optional primary call-to-action button
  version: number;
  interaction?: ArticleInteraction;
  course?: ArticleCourse; // present only on minikurs overviews + lessons
  training?: ArticleTraining; // present only on training passes + exercises
  recipe?: ArticleRecipe; // present only on recipes (structured recipe view)
  plan?: ArticlePlan; // present only on nutrition plans (structured week view)
  // TES-438/440 — "Källor (n)". Present only when the surface actually has
  // sources; absent means render no section at all (an empty "Källor (0)"
  // reads to a patient like something went missing). Articles, course lessons
  // and programs are all Article rows, so one field covers every surface.
  sources?: PatientSourceSection;
  // TES-1497 — see publicArticleSchema.
  related?: ArticleRelatedLink[];
  // TES-539 — see publicArticleSchema.
  libraryVisible?: boolean;
  // TES-591 — see publicArticleSchema.
  locked?: boolean;
}
export const publicArticleSchema = z.object({
  key: z.string(),
  type: articleTypeSchema,
  title: localizedTextSchema,
  body: localizedTextSchema,
  cta: articleCtaSchema.optional(),
  version: z.number().int().positive(),
  interaction: articleInteractionSchema.optional(),
  course: articleCourseSchema.optional(),
  training: articleTrainingSchema.optional(),
  recipe: articleRecipeSchema.optional(),
  plan: articlePlanSchema.optional(),
  sources: patientSourceSectionSchema.optional(),
  // TES-1497 — "Läs mer": the authored related articles, in authored order,
  // narrowed to targets that are published and readable by key. Served on the
  // by-key reads only (the lists do not carry it). Absent when nothing
  // resolves, so the reader renders no empty heading. Additive and optional:
  // a build that predates it never reads the field.
  related: z.array(articleRelatedLinkSchema).optional(),
  // TES-539 — the Kunskapsbank's visibility gate, projected from the authored
  // taxonomy (`library_visible` / `libraryVisible` in authorMetadata). Absent
  // means the author said nothing; the bank treats that as visible for a
  // knowledge article and irrelevant for everything else.
  libraryVisible: z.boolean().optional(),
  // TES-591 — true on a preview served to a patient without
  // SUBSCRIPTION_CONTENT_ACCESS: the content fields are empty on purpose.
  locked: z.boolean().optional(),
});

// TES-403 — submit/read a patient's answer. At least one of choice/text.
//
// v2.4 reflection contract: `visibility` is deliberately ABSENT from the
// submit shape. A save can only ever produce a PRIVATE reflection; moving one
// to the coach is a separate, explicit action (reflectionShareSchema), which
// is how "share_requires_explicit_action_each_time" is enforced structurally
// rather than by remembering to check a flag.
export const reflectionSubmitSchema = z
  .object({
    choice: z.string().min(1).max(200).optional(),
    text: z.string().min(1).max(2000).optional(),
    // Client-declared, unverifiable by design: OS keyboard dictation gives the
    // app no signal, so MVP always sends 'text'. Kept for a future real STT.
    inputMethod: reflectionInputMethodSchema.optional(),
  })
  .refine((v) => v.choice !== undefined || v.text !== undefined, {
    message: "choice or text is required",
  });
export type ReflectionSubmit = z.infer<typeof reflectionSubmitSchema>;

export const reflectionStateSchema = z.object({
  choice: z.string().nullable(),
  text: z.string().nullable(),
  updatedAt: z.string(),
  visibility: reflectionVisibilitySchema.default("private"),
  sharedAt: z.string().nullable().default(null),
});
export type ReflectionState = z.infer<typeof reflectionStateSchema>;

// One saved reflection as "Din resa" needs it: the answer plus enough source
// context to render "Reflektion · <artikeltitel>" without a second fetch.
export const patientReflectionSchema = z.object({
  id: z.string(),
  sourceType: reflectionSourceTypeSchema,
  articleKey: z.string(),
  courseKey: z.string().nullable(),
  lessonKey: z.string().nullable(),
  // Resolved server-side from the source Article, locale-aware.
  sourceTitle: z.string().nullable(),
  prompt: z.string().nullable(),
  promptId: z.string(),
  promptVersion: z.number().int().positive(),
  choice: z.string().nullable(),
  text: z.string().nullable(),
  inputMethod: reflectionInputMethodSchema,
  visibility: reflectionVisibilitySchema,
  sharedAt: z.string().nullable(),
  // v2.5 delta: which round of answering this is. 1 for the first; a new
  // dated entry created by "Reflektera igen" gets the next number, and the
  // earlier rounds stay in the list rather than being replaced.
  sequence: z.number().int().positive(),
  // Whether this entry can still be revised in place — true until 24 hours
  // after it was written (Drive Ella 2.1, TES-1423; it was the calendar day
  // of README 04 Din resa before). Computed
  // server-side so the rule lives in exactly one place — a client re-deriving
  // it from createdAt would be a second implementation free to disagree.
  // false ⇒ the action is "Reflektera igen" and starts a new entry.
  revisable: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PatientReflection = z.infer<typeof patientReflectionSchema>;

export const patientReflectionListSchema = z.object({
  reflections: z.array(patientReflectionSchema),
});
export type PatientReflectionList = z.infer<typeof patientReflectionListSchema>;

// Sharing a reflection into coach chat. The idempotency key is the client's
// (a stable per-tap uuid): a retried request returns the copy already
// delivered instead of posting a second bubble.
export const reflectionShareSchema = z.object({
  idempotencyKey: z.string().min(8).max(200),
});
export type ReflectionShare = z.infer<typeof reflectionShareSchema>;

export const reflectionShareResultSchema = z.object({
  reflectionId: z.string(),
  messageId: z.string(),
  version: z.number().int().positive(),
  sharedAt: z.string(),
  // True when the key matched an existing share, i.e. nothing new was posted.
  deduplicated: z.boolean(),
});
export type ReflectionShareResult = z.infer<typeof reflectionShareResultSchema>;
